import React, { useState } from 'react';
import { X, Calendar, User, Building2, Droplet, Package, CheckCircle, XCircle, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

const DetalleDesmancheModal = ({ open, onClose, desmanche }) => {
    const { toast } = useToast();
    const [isPrinting, setIsPrinting] = useState(false);

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('es-CO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const calcularDias = () => {
        if (desmanche.estado === 'Finalizado' && desmanche.dias) {
            return desmanche.dias;
        }
        
        const fechaRegistro = new Date(desmanche.fechaRegistro);
        const fechaActual = new Date();
        const diferenciaMilisegundos = fechaActual.getTime() - fechaRegistro.getTime();
        const dias = Math.ceil(diferenciaMilisegundos / (1000 * 60 * 60 * 24));
        
        return dias;
    };

    const handlePrint = async () => {
        setIsPrinting(true);
        try {
            const printer = getPrinterInstance();
            
            if (!printer.isConnected()) {
                toast({
                    title: 'Conectando impresora',
                    description: 'Seleccione su impresora Bluetooth...',
                });
                await printer.connect();
            }
            
            await printer.printDesmancheReceipt(desmanche);
            
            toast({
                title: 'Éxito',
                description: 'Resumen impreso correctamente',
            });
        } catch (error) {
            console.error('Error al imprimir:', error);
            toast({
                title: 'Error',
                description: error.message || 'No se pudo imprimir el resumen',
                variant: 'destructive',
            });
        } finally {
            setIsPrinting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Droplet className="h-5 w-5 text-orange-600" />
                        Detalle del Proceso de Desmanche
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Estado */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Estado:</span>
                        <Badge
                            className={
                                desmanche.estado === 'Finalizado'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-orange-100 text-orange-800'
                            }
                        >
                            {desmanche.estado}
                        </Badge>
                    </div>

                    {/* Información del Edificio */}
                    <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                            <Building2 className="h-4 w-4 text-blue-600" />
                            <span className="font-semibold text-blue-900">Edificio</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                                <span className="text-gray-600">Nombre:</span>
                                <p className="font-medium">{desmanche.edificio.nombre}</p>
                            </div>
                            {desmanche.edificio.ciudad && (
                                <div>
                                    <span className="text-gray-600">Ciudad:</span>
                                    <p className="font-medium">{desmanche.edificio.ciudad}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Información del Proceso */}
                    <div className="bg-orange-50 p-4 rounded-lg space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                            <Droplet className="h-4 w-4 text-orange-600" />
                            <span className="font-semibold text-orange-900">
                                Información del Desmanche
                            </span>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Tipo de Mancha:</span>
                                <span className="font-medium">{desmanche.tipoMancha}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Responsable:</span>
                                <span className="font-medium">{desmanche.responsable}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total de Prendas:</span>
                                <span className="font-bold">{desmanche.totalPrendas}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Fecha de Registro:</span>
                                <span className="font-medium">
                                    {formatDate(desmanche.fechaRegistro)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">
                                    {desmanche.estado === 'Finalizado' ? 'Días en Desmanche:' : 'Días Transcurridos:'}
                                </span>
                                <span className="font-medium">
                                    {calcularDias()} {calcularDias() === 1 ? 'día' : 'días'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Detalle de Prendas - Tabla */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Package className="h-5 w-5 text-gray-700" />
                            <span className="font-semibold text-lg">Detalle por Prenda</span>
                        </div>
                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="text-left p-3 text-sm font-semibold">Prenda</th>
                                        <th className="text-center p-3 text-sm font-semibold w-32">
                                            Total
                                        </th>
                                        {desmanche.estado === 'Finalizado' && (
                                            <>
                                                <th className="text-center p-3 text-sm font-semibold w-32 bg-green-50">
                                                    Recuperadas
                                                </th>
                                                <th className="text-center p-3 text-sm font-semibold w-32 bg-red-50">
                                                    No Recuperadas
                                                </th>
                                                <th className="text-center p-3 text-sm font-semibold w-24">
                                                    % Éxito
                                                </th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {desmanche.prendas.map((prenda, index) => {
                                        // Buscar el detalle de esta prenda si existe
                                        const detalle = desmanche.prendasDetalle?.find(d => d.id === prenda.id);
                                        const recuperadas = detalle?.cantidadRecuperada || 0;
                                        const noRecuperadas = detalle?.cantidadNoRecuperada || 0;
                                        const porcentajeExito = prenda.cantidad > 0 
                                            ? Math.round((recuperadas / prenda.cantidad) * 100) 
                                            : 0;
                                        
                                        return (
                                            <tr key={index} className="border-b hover:bg-gray-50">
                                                <td className="p-3">
                                                    <div className="flex items-center gap-2">
                                                        <Package className="h-4 w-4 text-gray-400" />
                                                        <span className="font-medium">{prenda.nombre}</span>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <span className="inline-flex items-center justify-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold text-sm">
                                                        {prenda.cantidad}
                                                    </span>
                                                </td>
                                                {desmanche.estado === 'Finalizado' && (
                                                    <>
                                                        <td className="p-3 text-center bg-green-50">
                                                            <span className="inline-flex items-center justify-center px-3 py-1 bg-green-100 text-green-700 rounded-full font-semibold text-sm">
                                                                {recuperadas}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-center bg-red-50">
                                                            <span className="inline-flex items-center justify-center px-3 py-1 bg-red-100 text-red-700 rounded-full font-semibold text-sm">
                                                                {noRecuperadas}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-center">
                                                            <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full font-bold text-xs ${
                                                                porcentajeExito === 100 
                                                                    ? 'bg-green-100 text-green-700' 
                                                                    : porcentajeExito >= 50
                                                                    ? 'bg-yellow-100 text-yellow-700'
                                                                    : 'bg-red-100 text-red-700'
                                                            }`}>
                                                                {porcentajeExito}%
                                                            </span>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                {desmanche.estado === 'Finalizado' && (
                                    <tfoot className="bg-gray-50 border-t-2">
                                        <tr>
                                            <td className="p-3 font-bold">TOTALES</td>
                                            <td className="p-3 text-center">
                                                <span className="inline-flex items-center justify-center px-3 py-1 bg-blue-200 text-blue-800 rounded-full font-bold text-sm">
                                                    {desmanche.totalPrendas}
                                                </span>
                                            </td>
                                            <td className="p-3 text-center bg-green-50">
                                                <span className="inline-flex items-center justify-center px-3 py-1 bg-green-200 text-green-800 rounded-full font-bold text-sm">
                                                    {desmanche.prendasRecuperadas || 0}
                                                </span>
                                            </td>
                                            <td className="p-3 text-center bg-red-50">
                                                <span className="inline-flex items-center justify-center px-3 py-1 bg-red-200 text-red-800 rounded-full font-bold text-sm">
                                                    {desmanche.prendasNoRecuperadas || 0}
                                                </span>
                                            </td>
                                            <td className="p-3 text-center">
                                                <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full font-bold text-xs ${
                                                    Math.round((desmanche.prendasRecuperadas / desmanche.totalPrendas) * 100) === 100
                                                        ? 'bg-green-200 text-green-800'
                                                        : Math.round((desmanche.prendasRecuperadas / desmanche.totalPrendas) * 100) >= 50
                                                        ? 'bg-yellow-200 text-yellow-800'
                                                        : 'bg-red-200 text-red-800'
                                                }`}>
                                                    {Math.round((desmanche.prendasRecuperadas / desmanche.totalPrendas) * 100)}%
                                                </span>
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                        {desmanche.estado === 'Finalizado' && desmanche.fechaFinalizacion && (
                            <div className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded">
                                <span className="font-medium">Fecha de Finalización:</span>{' '}
                                {formatDate(desmanche.fechaFinalizacion)}
                            </div>
                        )}
                    </div>

                    {/* Creador */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <User className="h-4 w-4 text-gray-600" />
                            <span className="font-semibold">Registrado por</span>
                        </div>
                        <p className="text-sm">{desmanche.creator.nombre}</p>
                    </div>

                    {/* Observaciones */}
                    {desmanche.observaciones && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <span className="font-semibold block mb-2">Observaciones</span>
                            <p className="text-sm text-gray-700">{desmanche.observaciones}</p>
                        </div>
                    )}
                </div>

                <div className="flex justify-between items-center mt-4">
                    <Button
                        variant="outline"
                        onClick={handlePrint}
                        disabled={isPrinting}
                        className="flex items-center gap-2"
                    >
                        <Printer className="h-4 w-4" />
                        {isPrinting ? 'Imprimiendo...' : 'Imprimir'}
                    </Button>
                    <Button onClick={onClose}>Cerrar</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default DetalleDesmancheModal;
